import React, { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { RootState } from '../redux/store';
import { useSelector } from '../redux/hooks';

const PageContainer = styled.div`
  width: 100%;
  min-height: 100vh;
  padding: 40px;
  background: #ffffff;
`;

const Title = styled.h1`
  font-family: 'IBM Plex Mono', monospace;
  font-size: 32px;
  color: #a13b16;
  text-align: center;
  margin-bottom: 30px;
`;

const FlexRow = styled.div`
  display: flex;
  gap: 40px;
  align-items: flex-start;
`;

const List = styled.div`
  flex: 1;
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  align-content: flex-start;
  row-gap: 10px;
`;

const ListItem = styled.div<{ $isHovered: boolean }>`
  padding: 8px 12px;
  border: 1px solid #ccc;
  border-radius: 8px;
  cursor: pointer;
  transition: background 0.2s;
  background: ${props => props.$isHovered ? '#f0e6e1' : '#f8f8f8'};
  white-space: nowrap;
`;

const Form = styled.form`
  flex: 0 0 300px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const Input = styled.input`
  padding: 10px;
  border-radius: 8px;
  border: 1px solid #ccc;
  font-size: 16px;
`;

const Button = styled.button`
  padding: 10px;
  border: none;
  border-radius: 8px;
  background: #a13b16;
  color: white;
  font-weight: bold;
  cursor: pointer;
  transition: background 0.2s;
`;

const DangerButton = styled(Button)`
  background: #c0392b;
`;

const CancelButton = styled(Button)`
  background: #999;
`;

const TopBar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
`;

interface CoverType {
  cover_type_id: number;
  type: string;
}

const CoverTypeListView: React.FC = () => {
  const accessToken = useSelector((state: RootState) => state.auth.accessToken);
  const [types, setTypes] = useState<CoverType[]>([]);
  const [selected, setSelected] = useState<CoverType | null>(null);
  const [input, setInput] = useState('');
  const [hoveredItem, setHoveredItem] = useState<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetch(`${process.env.REACT_APP_BASE_API_URL}/cover-types`)
      .then(res => res.json())
      .then(setTypes)
      .catch(err => console.error('Failed to fetch cover types:', err));
  }, []);

  const handleSelect = useCallback((type: CoverType) => {
    setSelected(type);
    setInput(type.type);
  }, []);

  const handleSubmit = useCallback((e: React.FormEvent) => {
    e.preventDefault();
    if (!input.trim()) return;

    const method = selected ? 'PUT' : 'POST';
    const url = selected
      ? `${process.env.REACT_APP_BASE_API_URL}/cover-types/${selected.cover_type_id}`
      : `${process.env.REACT_APP_BASE_API_URL}/cover-types`;

    fetch(url, {
      method,
      credentials: 'include',
      headers: { 
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify({ type: input })
    })
      .then(res => res.json())
      .then(data => {
        if (selected) {
          setTypes(prev => prev.map(t => t.cover_type_id === data.cover_type_id ? data : t));
        } else {
          setTypes(prev => [...prev, data]);
        }
        setSelected(null);
        setInput('');
      })
      .catch(err => console.error('Failed to save cover type:', err));
  }, [accessToken, input, selected]);

  const handleDelete = useCallback(() => {
    if (!selected) return;

    fetch(`${process.env.REACT_APP_BASE_API_URL}/cover-types/${selected.cover_type_id}`, {
      method: 'DELETE',
      credentials: 'include',
      headers: { 
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      }
    })
      .then(() => {
        setTypes(prev => prev.filter(t => t !== selected));
        setSelected(null);
        setInput('');
      })
      .catch(err => console.error('Failed to delete cover type:', err));
  }, [accessToken, selected]);

  const handleCancel = useCallback(() => {
    setSelected(null);
    setInput('');
  }, []);

  return (
    <PageContainer>
      <TopBar>
        <Title>Типи обкладинок</Title>
        <Button onClick={() => navigate('/admin')}>Головна</Button>
      </TopBar>
      <FlexRow>
        <List>
          {types.map(type => (
            <ListItem
              key={type.cover_type_id}
              $isHovered={hoveredItem === type.type}
              onClick={() => handleSelect(type)}
              onMouseEnter={() => setHoveredItem(type.type)}
              onMouseLeave={() => setHoveredItem(null)}
            >
              {type.type}
            </ListItem>
          ))}
        </List>
        <Form onSubmit={handleSubmit}>
          <Input
            placeholder="Тип обкладинки"
            value={input}
            onChange={e => setInput(e.target.value)}
          />
          <Button type="submit">
            {selected ? 'Зберегти' : 'Створити'}
          </Button>
          {selected && (
            <>
              <DangerButton type="button" onClick={handleDelete}>Видалити</DangerButton>
              <CancelButton type="button" onClick={handleCancel}>Відмінити</CancelButton>
            </>
          )}
        </Form>
      </FlexRow>
    </PageContainer>
  );
};

export default CoverTypeListView;